// components/AudioUpload.tsx
// Audio upload form with draft auto-save

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useFileUpload } from '../firebase/hooks';
import { useDrafts } from '../firebase/useDrafts';
import { useAuth } from '../contexts/AuthContext';
import { PostCategory } from '../types';
import './VideoUpload.css';
import { v4 as uuidv4 } from 'uuid';

export const AudioUpload: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const { uploadFile, uploadProgress, resetProgress } = useFileUpload();
    const { saveDraft, loadDraft, deleteDraft, autoSaveStatus } = useDrafts('audio');

    const [draftId, setDraftId] = useState<string | null>(null);
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [title, setTitle] = useState('');
    const [caption, setCaption] = useState('');
    const [category, setCategory] = useState<PostCategory>('writing-tips');
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Debug auth state
    useEffect(() => {
        console.log('Current user:', currentUser);
        console.log('User authenticated:', !!currentUser);
        console.log('User email:', currentUser?.email);
        console.log('User ID:', currentUser?.uid);
    }, [currentUser]);

    // Load draft if resuming
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const resumeDraftId = params.get('draft');

        if (resumeDraftId) {
            loadDraft(resumeDraftId).then(draft => {
                if (draft) {
                    setDraftId(draft.id);
                    setTitle(draft.title || '');
                    setCaption(draft.caption || '');
                    setCategory(draft.category);
                }
            });
        }
    }, [location.search, loadDraft]);


    // Auto-save should only be re-triggered by the text fields; read the
    // file selections and current draftId through a ref so they don't recreate
    // the callback (draftId in particular would otherwise cause a re-save loop).
    const autoSaveLatest = useRef({ audioFile, thumbnailFile, draftId });
    autoSaveLatest.current = { audioFile, thumbnailFile, draftId };

    const handleAutoSave = useCallback(async () => {
        if (!title && !caption) return;

        const { audioFile, thumbnailFile, draftId } = autoSaveLatest.current;
        const id = await saveDraft({
            title,
            caption,
            category,
            audioFileName: audioFile?.name,
            thumbnailFileName: thumbnailFile?.name,
        }, draftId || undefined);

        if (id && !draftId) {
            setDraftId(id);
        }
    }, [title, caption, category, saveDraft]);

    // Auto-save every 30 seconds
    useEffect(() => {
        if (title || caption) {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }

            autoSaveTimerRef.current = setTimeout(() => {
                handleAutoSave();
            }, 30000);
        }

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [title, caption, handleAutoSave]);

    const handleSaveDraft = async () => {
        const id = await saveDraft({
            title,
            caption,
            category,
            audioFileName: audioFile?.name,
            thumbnailFileName: thumbnailFile?.name,
        }, draftId || undefined);

        if (id) {
            alert('Draft saved successfully!');
            navigate('/dashboard');
        }
    };

    const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('audio/')) {
                setError('Please select a valid audio file');
                return;
            }
            if (file.size > 50 * 1024 * 1024) {
                setError('Audio file must be less than 50MB');
                return;
            }
            setAudioFile(file);
            setError('');
        }
    };

    const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('Thumbnail must be less than 5MB');
                return;
            }
            setThumbnailFile(file);
            setError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check authentication first
        if (!currentUser) {
            setError('You must be logged in to upload audio');
            return;
        }

        if (!audioFile || !thumbnailFile) {
            setError('Please select both audio and thumbnail files');
            return;
        }
        if (!title.trim() || !caption.trim()) {
            setError('Please fill in all required fields');
            return;
        }
        if (title.length < 5 || title.length > 100) {
            setError('Title must be between 5 and 100 characters');
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            const audioPath = `barkBoard/audio/${Date.now()}_${audioFile.name}`;
            const audioUrl = await uploadFile(audioFile, audioPath);

            const thumbnailPath = `barkBoard/thumbnails/${Date.now()}_${thumbnailFile.name}`;
            const thumbnailUrl = await uploadFile(thumbnailFile, thumbnailPath);

            const docId = uuidv4(); // Generate once
            const postData = {
                id: docId, // Use same ID
                type: 'audio',
                postType: 'admin',
                title: title.trim(),
                caption: caption.trim(),
                category,
                audioUrl,
                thumbnailUrl,
                authorName: 'Omore, the GOAT',
                authorEmail: currentUser.email || '',
                authorId: currentUser.uid,
                createdAt: serverTimestamp(),
                publishedAt: serverTimestamp(),
                status: 'published',
                isActive: true,
                viewCount: 0,
                complimentCounts: {
                    creative: 0,
                    funny: 0,
                    inspiring: 0,
                    smart: 0,
                    cool: 0,
                    brave: 0,
                    original: 0,
                    relatable: 0,
                    amazing: 0,
                    wholesome: 0,
                    artistic: 0,
                    magical: 0,
                    legendary: 0,
                    genius: 0,
                    epic: 0,
                    champion: 0,
                    total: 0
                },
            };

            // Use setDoc instead of addDoc
            await setDoc(doc(db, 'barkBoard', 'posts', 'items', docId), postData);

            if (draftId) {
                await deleteDraft(draftId);
            }

            setSuccess(true);
            resetProgress();

            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);

        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.message || 'Failed to upload audio. Please try again.');
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        navigate('/dashboard');
    };

    if (success) {
        return (
            <div className="upload-container">
                <div className="success-card">
                    <div className="success-icon">✅</div>
                    <h2>Audio Published Successfully!</h2>
                    <p>Your audio is now live.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="upload-container">
            <div className="upload-card">
                <div className="upload-header">
                    <h1>Upload Audio</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {autoSaveStatus === 'saving' && (
                            <span style={{ fontSize: '12px', color: '#718096' }}>💾 Saving...</span>
                        )}
                        {autoSaveStatus === 'saved' && (
                            <span style={{ fontSize: '12px', color: '#48bb78' }}>✓ Draft saved</span>
                        )}
                        <button onClick={handleCancel} className="close-button">×</button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="upload-form">
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <div className="form-section">
                        <label className="form-label">Audio File *</label>
                        <div className="file-upload-area">
                            <input
                                type="file"
                                accept="audio/*"
                                onChange={handleAudioChange}
                                disabled={isUploading}
                                id="audio-file"
                            />
                            <label htmlFor="audio-file" className="file-upload-label">
                                {audioFile ? (
                                    <span>🎵 {audioFile.name}</span>
                                ) : (
                                    <span>Click to select audio file (MP3, WAV, M4A - Max 50MB)</span>
                                )}
                            </label>
                        </div>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Thumbnail Image *</label>
                        <div className="file-upload-area">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                disabled={isUploading}
                                id="thumbnail-file"
                            />
                            <label htmlFor="thumbnail-file" className="file-upload-label">
                                {thumbnailFile ? (
                                    <span>🖼️ {thumbnailFile.name}</span>
                                ) : (
                                    <span>Click to select thumbnail (JPG, PNG - Max 5MB)</span>
                                )}
                            </label>
                        </div>
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="title">Title *</label>
                        <input
                            type="text"
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Story Writing Podcast Episode 1"
                            maxLength={100}
                            disabled={isUploading}
                            className="text-input"
                        />
                        <span className="char-count">{title.length}/100</span>
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="caption">Caption *</label>
                        <textarea
                            id="caption"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="Brief description of your audio content..."
                            rows={4}
                            disabled={isUploading}
                            className="textarea-input"
                        />
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="category">Category *</label>
                        <select
                            id="category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value as PostCategory)}
                            disabled={isUploading}
                            className="select-input"
                        >
                            <option value="writing-tips">Writing Tips</option>
                            <option value="story-inspiration">Story Inspiration</option>
                            <option value="creative-tools">Creative Tools</option>
                            <option value="character-development">Character Development</option>
                            <option value="world-building">World Building</option>
                            <option value="community-spotlight">Community Spotlight</option>
                        </select>
                    </div>

                    {uploadProgress && uploadProgress.status === 'uploading' && (
                        <div className="progress-section">
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress.progress}%` }}
                                />
                            </div>
                            <p className="progress-text">
                                Uploading {uploadProgress.fileName}... {uploadProgress.progress}%
                            </p>
                        </div>
                    )}

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={handleSaveDraft}
                            className="cancel-button"
                            disabled={isUploading}
                            style={{ background: '#667eea', color: 'white' }}
                        >
                            💾 Save Draft
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="cancel-button"
                            disabled={isUploading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="publish-button"
                            disabled={isUploading || !audioFile || !thumbnailFile || !title || !caption}
                        >
                            {isUploading ? 'Publishing...' : 'Publish Audio'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};